import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDropzone } from 'react-dropzone';

import Icon from '../../../icon/Icon';

import './PhotoUploadInput.scss';

export const MimeType = {
  anyImage: 'image/*',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  avif: 'image/avif',
};

export const Format = {
  dataUrl: 'dataUrl',
  file: 'file',
};

export const defaultAccept = [
  MimeType.jpeg,
  MimeType.png,
  MimeType.webp,
  MimeType.avif,
];

async function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result));
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(file);
  });
}

async function processFiles(files, format) {
  switch (format) {
    case Format.dataUrl:
      return Promise.all([...files].map(readAsDataUrl));
    case Format.file:
      return [...files];
    default:
      return files;
  }
}

const PhotoUploadInput = ({
  className,
  accept = defaultAccept,
  format = Format.file,
  disabled,
  onFiles,
  onError,
}) => {
  const handleDrop = useCallback(
    (files) => {
      if (files.length > 0) {
        processFiles(files, format).then(onFiles, onError);
      }
    },
    [format, onFiles, onError],
  );

  const { getInputProps, getRootProps } = useDropzone({
    accept,
    disabled,
    multiple: true,
    onDrop: handleDrop,
  });

  return (
    <div
      className={classnames(
        'photo-upload-input',
        disabled ? 'disabled' : '',
        className,
      )}
      {...getRootProps()}
    >
      <Icon name="camera" />
      Add a photo
      <input {...getInputProps()} />
    </div>
  );
};

PhotoUploadInput.propTypes = {
  className: PropTypes.string,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  accept: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  format: PropTypes.oneOf(Object.values(Format)),
  onFiles: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default PhotoUploadInput;
